import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { getSessionCode } from '@/lib/session';

const checklistItems = [
  'Bu sistemin klasik bir reklam ajansı hizmeti olmadığını; reklam, otomasyon, davranış analizi ve müşteri takip paneli mantığıyla çalıştığını anladım.',
  'Sistemin amacının sadece daha fazla mesaj almak değil, gelen kişilerin gerçekten potansiyel müşteri olup olmadığını analiz etmek olduğunu anladım.',
  'Reklamdan gelen kişilerin Instagram, WhatsApp, Facebook veya landing page üzerinden karşılanacağını ve davranışlarının analiz edileceğini anladım.',
  'Önce işime özel ücretsiz örnek bir karşılama ve yönlendirme sistemi hazırlanacağını, bu örneği test etmeden ödeme yapmayacağımı anladım.',
  'Ücretsiz örnek sistemi uygun bulursam, ilk ay başlangıç bedelinin 5.000 TL olduğunu anladım.',
  'Reklam bütçesinin bu bedele dahil olmadığını, reklam harcamasının ayrıca benim reklam hesabımdan yapılacağını anladım.',
  'İlk ay içinde sosyal medya karşılama otomasyonu, WhatsApp / Instagram / Facebook yönlendirme yapısı, reklam içerikleri, davranış analizi ve müşteri takip paneli gibi yapıların işime göre hazırlanacağını anladım.',
  'Sistemin daha iyi çalışabilmesi için panelde gelen başvuruları olumlu, olumsuz, satışa döndü, tekrar aranacak gibi basit şekilde işaretlemem gerektiğini anladım.',
  'Hiçbir reklam çalışmasında garanti satış sözü verilemeyeceğini; bu sistemin veriye, davranış analizine ve gerçek geri dönüşlere göre optimize edildiğini anladım.',
  'İlk ay sonunda memnun kalırsam ikinci aydan itibaren aylık hizmet bedelinin 10.000 TL olduğunu, memnun kalmazsam herhangi bir taahhüt veya devam zorunluluğu olmadığını anladım.',
];

const whatsappMessage = encodeURIComponent(
  'Merhaba, sisteminizi inceledim. Şartları okudum ve benim için uygundur. Ücretsiz örnek sistem hazırlığı için bilgi vermek istiyorum.'
);
const whatsappUrl = `https://wa.me/905069992900?text=${whatsappMessage}`;

interface ApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ApprovalModal({ isOpen, onClose }: ApprovalModalProps) {
  const [checked, setChecked] = useState<boolean[]>(new Array(checklistItems.length).fill(false));
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setChecked(new Array(checklistItems.length).fill(false));
      setShowWarning(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const toggleItem = useCallback((index: number) => {
    setChecked((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
    setShowWarning(false);
  }, []);

  const checkedCount = checked.filter(Boolean).length;
  const totalCount = checklistItems.length;
  const allChecked = checkedCount === totalCount;

  const handleApproveClick = useCallback(() => {
    if (!allChecked) {
      setShowWarning(true);
      return;
    }

    const sessionCode = getSessionCode();

    supabase
      .from('sh_satis_leadleri')
      .insert({
        firma_adi: 'Web Onay - ' + sessionCode,
        uygundur_yazdi: true,
        funnel_tamamlandi: true,
        funnel_onaylandi: true,
        lead_durumu: 'uygundur_yazdi',
        lead_skoru: 75,
        kaynak: 'landing_page',
        kaynak_durum: 'checklist_onaylandi',
        oturum_kodu: sessionCode,
        aktif: true,
        notlar: 'Checklist onaylandı ve WhatsApp\'a yönlendirildi.',
      })
      .then(({ error }) => {
        if (error) {
          console.error('Supabase insert error:', error.message);
        }
      });

    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    onClose();
  }, [allChecked, onClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-foreground-950/60 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-background-50 rounded-2xl overflow-hidden flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-500 hover:text-foreground-800 transition-colors duration-200 cursor-pointer z-10"
          aria-label="Kapat"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        <div className="px-6 md:px-8 pt-8 pb-2 flex-shrink-0">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-line text-accent-600 text-base"></i>
            </div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
              Devam Etmeden Önce Lütfen Kontrol Edin
            </h2>
          </div>
          <p className="text-foreground-600 text-sm leading-relaxed mb-4">
            WhatsApp'a geçmeden önce sistemin temel şartlarını anladığınızı onaylamanız gerekiyor. Bu adım, sürece gerçekten uygun firmalarla daha doğru ilerleyebilmemiz için kullanılır.
          </p>

          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-foreground-500">
              {checkedCount} / {totalCount} madde onaylandı
            </span>
            <div className="w-32 h-1.5 rounded-full bg-background-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-accent-500 transition-all duration-300 ease-out"
                style={{ width: `${(checkedCount / totalCount) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="px-6 md:px-8 overflow-y-auto flex-1">
          <div className="space-y-1.5 pb-2">
            {checklistItems.map((item, index) => (
              <label
                key={index}
                onClick={() => toggleItem(index)}
                className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 border ${
                  checked[index]
                    ? 'bg-accent-50 border-accent-200'
                    : 'bg-background-50 border-background-200 hover:border-background-300 hover:bg-background-100'
                }`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <div
                    className={`w-5 h-5 rounded-md flex items-center justify-center transition-all duration-200 border-2 ${
                      checked[index]
                        ? 'bg-accent-500 border-accent-500'
                        : 'border-foreground-300 bg-background-50'
                    }`}
                  >
                    {checked[index] && <i className="ri-check-line text-background-50 text-xs font-bold"></i>}
                  </div>
                </div>
                <span
                  className={`text-sm leading-relaxed select-none transition-colors duration-200 ${
                    checked[index] ? 'text-foreground-800' : 'text-foreground-600'
                  }`}
                >
                  {item}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="px-6 md:px-8 py-4 border-t border-background-200 flex-shrink-0 bg-background-50">
          <div className="flex items-center gap-2 mb-3">
            <div
              className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border-2 transition-all duration-200 ${
                allChecked ? 'bg-accent-500 border-accent-500' : 'border-foreground-300 bg-background-50'
              }`}
            >
              {allChecked && <i className="ri-check-line text-background-50 text-xs font-bold"></i>}
            </div>
            <span
              className={`text-sm font-medium transition-colors duration-200 ${
                allChecked ? 'text-foreground-900' : 'text-foreground-500'
              }`}
            >
              Yukarıdaki maddeleri okudum, anladım ve sistemin benim için uygun olduğunu düşünüyorum.
            </span>
          </div>

          {showWarning && (
            <div className="flex items-center gap-2 mb-3 p-2.5 rounded-lg bg-accent-100/80 border border-accent-200">
              <i className="ri-error-warning-line text-accent-600 text-sm flex-shrink-0"></i>
              <span className="text-accent-800 text-xs font-medium">
                Devam etmek için lütfen tüm maddeleri onaylayın.
              </span>
            </div>
          )}

          <button
            onClick={handleApproveClick}
            disabled={!allChecked}
            className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
              allChecked
                ? 'bg-accent-500 text-background-50 hover:bg-accent-600 shadow-lg shadow-accent-500/20'
                : 'bg-foreground-200 text-foreground-400 cursor-not-allowed'
            }`}
          >
            <i className="ri-whatsapp-line text-lg"></i>
            WhatsApp'a Geç ve Uygundur Yaz
          </button>
        </div>
      </div>
    </div>
  );
}